import { Alert, Box, Heading, Link, Text } from '@televet/kibble-ui';
import React, { useCallback, useMemo } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { useTracking } from 'shared/providers/TrackingProvider';
import { ServiceReminderEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useGetReminderSettingsClinicServicesFormTemplateIdQuery } from 'shared/types/graphql';

export const ReminderAppointmentRequestFormLink = ({
  clinicId,
}: {
  clinicId: string | undefined;
}): JSX.Element | null => {
  const { currentClinic } = useClinicUser();
  const { isServiceRemindersDisabledForEzyvet, primaryIntegration, primaryIntegrationName } = useIntegrationsProvider();

  const { data: clinicServicesFormTemplateData } = useGetReminderSettingsClinicServicesFormTemplateIdQuery({
    variables: { clinicId },
  });
  const { trackEvent } = useTracking();

  const formTemplateId = useMemo(() => {
    const formTemplate = clinicServicesFormTemplateData?.findFirstFormTemplate;
    if (formTemplate?.isActive && !formTemplate.isDeleted) {
      return formTemplate.id;
    }
  }, [clinicServicesFormTemplateData]);

  const onNavigateToFormTemplateClick = useCallback(() => {
    trackEvent(ServiceReminderEvents.ViewAppointmentRequestFormClick, {
      clinicId,
      integrationType: primaryIntegration?.type,
      clinicName: currentClinic?.name,
      pims: primaryIntegrationName || '',
    });
  }, [trackEvent, clinicId, primaryIntegration?.type, currentClinic?.name, primaryIntegrationName]);

  return (
    <Box>
      <Heading size="xs" mb={1}>
        Appointment Request Form
      </Heading>
      <Text as="p" maxWidth="500px" variant="subtle" mb={3} size="sm">
        Clients are required to fill out this form when requesting an appointment from a service reminder.
      </Text>
      {formTemplateId && !isServiceRemindersDisabledForEzyvet ? (
        <Link as={ReactRouterLink} to={`/forms/${formTemplateId}`} size="sm" onClick={onNavigateToFormTemplateClick}>
          View Appointment Request Form
        </Link>
      ) : (
        <Alert
          title="Appointment Request Form is Missing"
          status="warning"
          hideCloseButton
          description="Please contact support to have the form created."
        />
      )}
    </Box>
  );
};
